import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from "react-router-dom";
import { showNotify, updateProjectsStatus } from 'vendor/application/disptach';
import { langObject } from 'vendor/application';
import { ProjectAPI } from 'domains/Project/infrastructure';
import {
    Button,
    Card,
    CardActions,
    TextField,
    Divider,
    FormControl,
    Select,
    Switch,
    MenuItem,
    CircularProgress
} from '@material-ui/core';

import PropTypes from 'prop-types';

class ProjectEdit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            project: {},
            id: this.props.match.params.id,
            isSaving: false,
            isReplicating: false,
            lang: langObject()
        };
        this.getProject = this.getProject.bind(this);
        this.saveProject = this.saveProject.bind(this);
    }

    componentDidMount() {
        this.getProject();
    }

    async getProject() {
        const { access } = this.props.data;
        try {
            const obj = new ProjectAPI(this.props.match.params.id, access, this.props);
            const project = await obj.get_project_by_id();
            this.setState({ project });
        } catch (err) {
            console.error('Error fetching project:', err);
            this.errorRequest();
        }
    }

    updateElement(e, type) {
        const value = type === 'high_performance' ? e.target.checked : e.target.value;
        this.setState(prevState => ({
            project: { ...prevState.project, [type]: value }
        }));
    }

    errorRequest = () => {
        this.props.history.push('/projects');
        this.props.dispatch(showNotify({ message: `The project doesn't exist.`, severity: 'error' }));
    };

    returnToList = () => {
        const { history } = this.props;
        this.props.dispatch(updateProjectsStatus(true));
        this.props.getAllProjects();
        history.push('/projects');
    };

    async saveProject(e) {
        e.preventDefault();
        const { access } = this.props.data;
        this.setState({ isSaving: true });
        try {
            const payload = { ...this.state.project };
            if ('centribot_project_id' in payload) {
                const trimmed = (payload.centribot_project_id || '').trim();
                payload.centribot_project_id = trimmed || null;
            }
            const obj = new ProjectAPI(this.state.project.id, access, this.props, payload);
            await obj.put_project();

            this.props.dispatch(showNotify({
                message: 'The project was saved successfully.',
                severity: 'success'
            }));
        } catch (err) {
            this.props.dispatch(showNotify({
                message: err.message || 'Unexpected error occurred',
                severity: 'error'
            }));
        } finally {
            this.setState({ isSaving: false });
        }
    }

    async replicateProject() {
        const { access } = this.props.data;
        const { project } = this.state;

        if (!project.centribot_project_id) {
            this.props.dispatch(showNotify({ message: 'Project ID is not defined.', severity: 'error' }));
            return;
        }

        const confirmAction = window.confirm('Are you sure you want to replicate this project?');
        if (!confirmAction) return;

        this.setState({ isReplicating: true });

        try {
            const obj = new ProjectAPI(project.id, access, this.props, project);
            await obj.replicate_project();
            this.props.dispatch(showNotify({ message: 'Project replicated successfully.', severity: 'success' }));
        } catch (err) {
            this.props.dispatch(showNotify({ message: err.message || 'Unexpected error occurred', severity: 'error' }));
        } finally {
            this.setState({ isReplicating: false });
        }
    }

    render() {
        const { project, isSaving, isReplicating, lang } = this.state;
        return (
            <React.Fragment>
                {project && (
                    <Card elevation={3} className="cen-action-card">
                        <div className="cen-border"></div>
                        <div className="cen-action-header">
                            <h1>{'Project edit'}</h1>
                            <CardActions>
                                <Button className="cen-btn-cancel" variant="outlined" onClick={this.returnToList}>CANCEL</Button>
                                <Button className="cen-btn-create" variant="outlined" onClick={this.saveProject} disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={21} /> : 'SAVE'}
                                </Button>
                            </CardActions>
                        </div>
                        <div className="cen-action-body">
                            {/* Dialog Flow ID */}
                            <div className="cen-action-element">
                                <div>
                                    <span>Dialog flow ID</span>
                                    <span>Agent Id from Dialogflow project.</span>
                                </div>
                                <TextField
                                    value={project.dialog_flow_id || ''}
                                    onChange={(e) => this.updateElement(e, 'dialog_flow_id')}
                                />
                            </div>
                            <Divider />
                            {/* Centribot Project ID */}
                            <div className="cen-action-element">
                                <div>
                                    <span>Centribot Project ID</span>
                                    <span>Project Id from Centribot account.</span>
                                </div>
                                <TextField
                                    value={project.centribot_project_id || ''}
                                    onChange={(e) => this.updateElement(e, 'centribot_project_id')}
                                />
                            </div>
                            <Divider />
                            {/* Language */}
                            <div className="cen-action-element">
                                <div>
                                    <span>Language</span>
                                    <span>Language set in Dialogflow Agent.</span>
                                </div>
                                <FormControl>
                                    <Select
                                        value={project.lang || 'es'}
                                        onChange={(e) => this.updateElement(e, 'lang')}
                                    >
                                        {lang.map(i => (
                                            <MenuItem key={i} value={i}>{i}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <Divider />
                            {/* High Performance */}
                            <div className="cen-action-element">
                                <div>
                                    <span>Is High Performance?</span>
                                    <span>Indicates if the project is set for high performance mode.</span>
                                </div>
                                <Switch
                                    checked={Boolean(project.high_performance)}
                                    onChange={(e) => this.updateElement(e, 'high_performance')}
                                />
                            </div>
                            <Divider />
                            {/* Credentials */}
                            <div className="cen-action-element-grid">
                                <div>
                                    <span>Credentials</span>
                                    <span>Dialogflow credential JSON file content for authentication.</span>
                                </div>
                                <TextField
                                    multiline
                                    value={project.credentials_content || ''}
                                    variant="outlined"
                                    placeholder="copy and paste dialogflow credential file content"
                                    onChange={(e) => this.updateElement(e, 'credentials_content')}
                                />
                            </div>
                            <Divider />
                            {/* Replicate */}
                            {project.high_performance && (
                                <div
                                    className="cen-action-element-grid"
                                    style={{ backgroundColor: '#d2444458', padding: '20px' }}
                                >
                                    <div>
                                        <span>Replicate Accounts</span>
                                        <span>
                                            This action replicates Dialogflow agents from this account to all accounts with the same project_id.
                                        </span>
                                    </div>
                                    <Button
                                        className="cen-btn-replicate"
                                        variant="contained"
                                        color="secondary"
                                        style={{ width: '150px' }}
                                        onClick={() => this.replicateProject()}
                                        disabled={isReplicating}
                                    >
                                        {isReplicating ? (
                                            <CircularProgress size={21} color="inherit" />
                                        ) : (
                                            'REPLICATE'
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>
                )}
            </React.Fragment>
        );
    }
}

const mapStateToProps = state => ({ data: state });

ProjectEdit.propTypes = {
    getAllProjects: PropTypes.func,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};

export default withRouter(connect(mapStateToProps)(ProjectEdit));